import * as React from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, CircularProgress, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { MultiSelect } from "react-multi-select-component"; // Install this library
import { authHeader } from "../../helpers";
import { apiConstants } from "../../constants";
import dayjs from "dayjs";
import MenuItem from "@mui/material/MenuItem";

const columns = [
  {
    field: "category",
    headerName: "Category Name",
    width: 200,
    valueGetter: (params) => `${params.row.category || ""}`,
  },
  {
    field: "name",
    headerName: "Item Name",
    width: 200,
    valueGetter: (params) => `${params.row.name || ""}`,
  },
  {
    field: "cost",
    headerName: "Cost",
    width: 200,
    valueGetter: (params) => `${params.row.cost || ""}`,
  },
];

const COGSReport = () => {
  const [rows, setRows] = React.useState([]);
  const [response, setResponse] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const [filterByDates, setFilterByDates] = React.useState({
    start_date: "",
    end_date: "",
  });
  const [selectedItems, setSelectedItems] = React.useState([]);
  const [ItemsOptions, setItemsOptions] = React.useState([]);

  React.useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await fetch(`${apiConstants.API_URL}/items`, {
          method: "GET",
          headers: authHeader(),
        });
        const data = await response.json();

        setItemsOptions(
          data?.data.map((item) => ({
            label: item.name,
            value: item.id,
          }))
        );
      } catch (error) {
        console.error("Error fetching Items:", error);
      }
    };
    fetchItems();
  }, []);

  const handleGetReport = async () => {
    setLoading(true);

    const itemIds = selectedItems.map((item) => item.value).join(",");
    const queryParams = new URLSearchParams({
      start_date: filterByDates.start_date,
      end_date: filterByDates.end_date,
      item_ids: itemIds,
    });

    try {
      const response = await fetch(
        `${apiConstants.API_URL}/cogs-report?${queryParams}`,
        { method: "GET", headers: authHeader() }
      );
      const data = await response.json();
      setRows(data?.data || []);
      setResponse(data?.meta || {});
    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          margin: "14px 0px 16px",
          backgroundColor: "#EFEFEF",
          padding: "16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          {/* Start Date Picker */}
          <DatePicker
            label="Start Date"
            id="start-date"
            renderInput={(params) => (
              <TextField {...params} variant="standard" />
            )}
            value={dayjs(filterByDates.start_date)}
            onChange={(newValue) => {
              setFilterByDates({
                ...filterByDates,
                start_date: newValue
                  ? dayjs(newValue).format("YYYY-MM-DD")
                  : "",
              });
            }}
          />

          {/* End Date Picker */}
          <DatePicker
            label="End Date"
            id="end-date"
            renderInput={(params) => (
              <TextField {...params} variant="standard" />
            )}
            value={dayjs(filterByDates.end_date)}
            onChange={(newValue) => {
              setFilterByDates({
                ...filterByDates,
                end_date: newValue ? dayjs(newValue).format("YYYY-MM-DD") : "",
              });
            }}
          />

          {/* item MultiSelect */}
          <MultiSelect
            options={ItemsOptions}
            value={selectedItems}
            onChange={setSelectedItems}
            labelledBy="Select Items"
            sx={{ width: "100%" }}
            className="multi-select-container"
          />

          {/* Get Report Button */}
          <Button
            variant="contained"
            sx={{ height: "42px" }}
            onClick={handleGetReport}
          >
            Get Report
          </Button>
        </Box>
      </Box>

      <Box style={{ width: "100%" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "300px",
            }}
          >
            <CircularProgress size={50} />
          </Box>
        ) : (
          <DataGrid
            autoHeight
            rows={rows}
            pageSize={response?.per_page || 10}
            pagination
            getRowId={(row) => row.id}
            rowCount={response?.total || 0}
            columns={columns}
            paginationMode="server"
          />
        )}
      </Box>
    </>
  );
};

export default COGSReport;
